<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBranding } from 'shared/composables/useBranding';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { picoSearch } from '@chatwoot/pico-search';
import IntegrationItem from './IntegrationItem.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { replaceInstallationName } = useBranding();
const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

// Customer Context isn't part of Chatwoot's hook-based apps.yml system (it
// has its own dedicated settings page/controller, not a generic
// settings_json_schema form) — the apps API never lists it, so it needs a
// manually-added card here or it's only reachable by typing the URL
// directly (see plans/ellie-desk-integrations-self-service-plan.md).
const customIntegrationList = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CUSTOMER_CONTEXT)
    ? [
        {
          id: 'customer_context',
          name: t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.HEADER'),
          description: t('INTEGRATION_SETTINGS.CUSTOMER_CONTEXT.DESCRIPTION'),
          enabled: true,
        },
      ]
    : []
);

// Brands is likewise not an apps.yml hook — it configures which knowledge and
// voice each inbox's AI uses, so it needs a card here to be discoverable.
const brandsIntegration = computed(() => [
  {
    id: 'brands',
    name: t('INTEGRATION_SETTINGS.BRANDS.HEADER'),
    description: t('INTEGRATION_SETTINGS.BRANDS.DESCRIPTION'),
    enabled: true,
  },
]);

const integrationList = computed(() => [
  ...getters['integrations/getAppIntegrations'].value,
  ...customIntegrationList.value,
  ...brandsIntegration.value,
]);

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
